import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Flag, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatBRL, formatDateTime, formatDeadline } from "@/lib/format";
import { formatPosition, type LiveEntry } from "@/lib/liveLeaderboard";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/Toast";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ScreenSpinner } from "@/components/ui/Spinner";
import type { Tournament } from "@/types";

type MyBet = {
  id: string;
  tournamentId: string;
  playerIds: string[];
  playerNames: string[];
  amount: number;
  createdAt: string;
};

// Meus palpites: todas as apostas do usuário (GET /api/me/bets) com o
// torneio resolvido; cancelamento permitido enquanto o deadline não passou
// (DELETE /api/bets/:id — refund automático server-side). Com o torneio em
// andamento, cada atleta do card mostra a posição ao vivo ("T#") na primeira
// coluna; sem feed (torneio ainda não começou), a coluna não aparece.
export default function MeusPalpitesPage() {
  const { refreshUser } = useAuth();
  const { showToast } = useToast();
  const [bets, setBets] = useState<MyBet[]>([]);
  const [tournaments, setTournaments] = useState<Map<string, Tournament>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  // tournamentId → (playerId → posição ao vivo). Só tem entrada quando o
  // feed do torneio retornou linhas; posição null = CUT/WD/DQ.
  const [positions, setPositions] = useState<
    Map<string, Map<string, number | null>>
  >(new Map());
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // Grupos de torneio ENCERRADO começam colapsados (pedido do produto): o
  // histórico cresce a cada semana e enterrava o torneio em andamento.
  // Guarda os que o usuário abriu manualmente.
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggleGroup = (tournamentId: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(tournamentId)) next.delete(tournamentId);
      else next.add(tournamentId);
      return next;
    });

  const load = useCallback(() => {
    return Promise.all([
      apiFetch<{ bets: MyBet[] }>("/api/me/bets"),
      apiFetch<Tournament[]>("/api/tournaments", { auth: false }),
    ])
      .then(async ([b, ts]) => {
        setBets([...b.bets].reverse()); // mais recentes primeiro
        setTournaments(new Map(ts.map((t) => [t.id, t])));
        // Feed ao vivo por torneio apostado — vazio/falha = sem coluna de
        // posição para aquele torneio.
        const ids = [...new Set(b.bets.map((x) => x.tournamentId))];
        const results = await Promise.all(
          ids.map((id) =>
            apiFetch<LiveEntry[]>(
              `/api/tournaments/${encodeURIComponent(id)}/leaderboard`,
              { auth: false },
            )
              .then((rows) => [id, rows] as const)
              .catch(() => [id, [] as LiveEntry[]] as const),
          ),
        );
        const byTournament = new Map<string, Map<string, number | null>>();
        for (const [id, rows] of results) {
          if (rows.length === 0) continue;
          const byPlayer = new Map<string, number | null>();
          for (const e of rows) byPlayer.set(e.playerId, e.position);
          byTournament.set(id, byPlayer);
        }
        setPositions(byTournament);
      })
      .catch(() => {
        showToast({ type: "error", message: "Erro ao carregar palpites." });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load().finally(() => setLoading(false));
  }, [load]);

  const cancel = async (betId: string) => {
    setCancelingId(betId);
    try {
      const res = await apiFetch<{ refunded: number }>(`/api/bets/${betId}`, {
        method: "DELETE",
      });
      await Promise.all([load(), refreshUser()]);
      showToast({
        type: "success",
        message: `Palpite excluído. ${formatBRL(res.refunded)} devolvidos ao saldo.`,
      });
    } catch (err) {
      showToast({
        type: "error",
        message:
          err instanceof Error ? err.message : "Não foi possível excluir.",
      });
    } finally {
      setCancelingId(null);
      setConfirmId(null);
    }
  };

  const groups = useMemo(() => {
    const byTournament = new Map<string, MyBet[]>();
    for (const b of bets) {
      const list = byTournament.get(b.tournamentId) ?? [];
      list.push(b);
      byTournament.set(b.tournamentId, list);
    }
    return [...byTournament.entries()];
  }, [bets]);

  if (loading) return <ScreenSpinner />;

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <div className="flex items-center gap-3">
        <Link
          to="/"
          aria-label="Voltar"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </Link>
        <h1 className="text-xl font-bold">Meus palpites</h1>
      </div>

      {groups.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-(--radius-app) border border-border bg-card px-6 py-12 text-center">
          <Flag size={32} className="text-muted-foreground" />
          <p className="text-sm font-semibold">Nenhum palpite ainda</p>
          <p className="text-xs text-muted-foreground">
            Escolha seu Top 3 no torneio da semana e entre na disputa.
          </p>
          <Link to="/" className="mt-2">
            <Button title="Ver torneio da semana" />
          </Link>
        </div>
      )}

      {groups.map(([tournamentId, list]) => {
        const t = tournaments.get(tournamentId);
        const cancellable =
          t &&
          (t.status === "upcoming" || t.status === "active") &&
          new Date(t.deadline).getTime() > Date.now();
        // Encerrado = confirmação oficial (finished_at) ou status settled.
        const isFinished = !!t && (!!t.finishedAt || t.status === "settled");
        const collapsed = isFinished && !expandedGroups.has(tournamentId);
        const chip = t && (
          <Chip
            tone={
              t.status === "active"
                ? "success"
                : t.status === "upcoming"
                  ? "accent"
                  : "muted"
            }
          >
            {t.status === "active"
              ? "EM ANDAMENTO"
              : t.status === "upcoming"
                ? "EM BREVE"
                : "ENCERRADO"}
          </Chip>
        );
        return (
          <section key={tournamentId}>
            {isFinished ? (
              <button
                type="button"
                onClick={() => toggleGroup(tournamentId)}
                aria-expanded={!collapsed}
                className="mb-2 flex w-full items-center gap-2 text-left"
              >
                <h2 className="min-w-0 flex-1 truncate text-sm font-bold">
                  {t?.name ?? tournamentId}
                </h2>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {list.length === 1 ? "1 palpite" : `${list.length} palpites`}
                </span>
                {chip}
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-muted-foreground transition-transform ${
                    collapsed ? "" : "rotate-180"
                  }`}
                />
              </button>
            ) : (
              <div className="mb-2 flex items-center justify-between">
                <h2 className="min-w-0 truncate text-sm font-bold">
                  {t?.name ?? tournamentId}
                </h2>
                {chip}
              </div>
            )}
            {t && cancellable && (
              <p className="mb-2 text-xs text-muted-foreground">
                Palpites até {formatDeadline(t.deadline)}
              </p>
            )}
            <div className={`flex flex-col gap-2 ${collapsed ? "hidden" : ""}`}>
              {list.map((bet) => (
                <Card key={bet.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      {bet.playerIds.map((pid, i) => {
                        const posByPlayer = positions.get(bet.tournamentId);
                        const pos = posByPlayer?.get(pid);
                        return (
                          <p
                            key={pid}
                            className="flex min-w-0 items-center gap-2 text-sm font-semibold"
                          >
                            {posByPlayer && (
                              <span className="w-8 shrink-0 text-xs font-semibold text-muted-foreground">
                                {pos != null ? formatPosition(pos) : "—"}
                              </span>
                            )}
                            <span className="truncate">
                              {bet.playerNames[i]?.trim() || pid}
                            </span>
                          </p>
                        );
                      })}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-bold">
                        {formatBRL(bet.amount)}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {formatDateTime(bet.createdAt)}
                      </p>
                    </div>
                  </div>
                  {cancellable && (
                    <div className="mt-3 border-t border-border pt-3">
                      {confirmId === bet.id ? (
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-muted-foreground">
                            Excluir e devolver {formatBRL(bet.amount)}?
                          </p>
                          <div className="flex gap-2">
                            <Button
                              title="Não"
                              variant="ghost"
                              className="h-9 px-3 text-xs"
                              onClick={() => setConfirmId(null)}
                            />
                            <Button
                              title="Sim, excluir"
                              variant="danger"
                              className="h-9 px-3 text-xs"
                              loading={cancelingId === bet.id}
                              onClick={() => void cancel(bet.id)}
                            />
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmId(bet.id)}
                          className="flex items-center gap-1.5 text-xs font-medium text-destructive"
                        >
                          <Trash2 size={14} /> Excluir palpite
                        </button>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
